import {
  Component, ComponentInterface, h, Prop, State, Event, EventEmitter,
  Fragment,
} from '@stencil/core';
import type { TCategoryAllData } from '@pes-ui/components/dist/types/components/pes-app-category/types';
import type { TCategoryListingAllData } from '@pes-ui/components/dist/types/components/pes-app-category-listing/types';
import type { IProductsCarouselData } from '@pes-ui/components/dist/types/components/products-carousel/types';
import { buildPageViewPayload, consoleErrorMsg, send, HmcCategories, TProductCtas, pesCatalogPageTypes, HMCPlatform, PageNames } from '@pes-ui/utils';
import type { TSearchParams, THMCBanner } from '@pes-ui/utils';

import { RecentlyViewedConfig } from '@pes-ui/components/dist/types/global-types/common';
import { PesRoute } from '@pes-ui/components/dist/collection';

import { expAPIEndpoint, getExpAPIEndpointURL } from '../../../globals/utils/exp-api-urls-builder';

import { MY_SCHNEIDER_CLICK_EVENT } from '../../../globals/constants';
import { CategoryContextTemplate, CategoryInfoObject } from './types';
import {
  FILTER_QUERY_PARAM,
  MAX_RECENTLY_VIEW_PRODUCTS,
  getData,
  requestRecentlyViewedProducts,
} from '../../../globals/utils';

@Component({
  tag: 'pes-app-spryker-category-container',
  shadow: true,
})
export class PesAppSprykerCategoryContainer implements ComponentInterface {
  /**
   * The host of the endpoints.
   * @example https://www.se.com:9090
   */
  @Prop() host!: string;

  /**
   * The code of the country of the app.
   * @example fr
   */
  @Prop() countryCode!: string;

  /**
   * The code of the language of the app.
   * @example fr
   */
  @Prop() languageCode!: string;

  /**
   * The ID of the category.
   * @example 4200
   */
  @Prop() categoryId!: string;

  /**
   * Setup productCtas logic.
   * buttons - predefined CTA buttons.
   * productProp - predefined property of product to find in list.
   */
  @Prop() productCtas: TProductCtas;

  /**
   * Defines whether to show the breadcrumbs.
   */
  @Prop() showBreadcrumbs: boolean;

  /**
   * Defines whether to show the documents.
   */
  @Prop() showDocuments = true;

  /**
   * Defines whether to show the commerce connector.
   */
  @Prop() enableCommerceConnector = true;

  /**
   * Defines whether to show the buy online.
   */
  @Prop() showBuyOnline = true;

  /**
   * Defines whether to show the compare.
   */
  @Prop() showCompare = true;

  @Prop() showRecentlyViewed!: boolean;

  @Prop() searchParams: TSearchParams = {};

  @State() categoryAllData: TCategoryAllData | TCategoryListingAllData = null;

  @State() recentlyViewedConfig: RecentlyViewedConfig = null;

  @State() recentlyViewedProducts: IProductsCarouselData = {
    sectionTitle: '',
    data: [],
    versionLabel: '',
  };

  @Event({
    eventName: MY_SCHNEIDER_CLICK_EVENT,
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    pesLinkClick: EventEmitter<PesRoute>;

  componentWillLoad(): void {
    this.init();
  }

  emitPageViewEvent() {
    send(
      buildPageViewPayload,
      {
        pageLanguage: this.languageCode,
        pageTitle: this.categoryAllData.metaTags.analyticsPageData.pageTitle,
        fromWebView: this.categoryAllData.metaTags.analyticsPageData.fromWebView,
        businessUnit: this.categoryAllData.metaTags.analyticsPageData.businessUnit,
        template: this.categoryAllData.metaTags.analyticsPageData.template,
        pageTopCategory: this.categoryAllData.metaTags.analyticsPageData.pageTopCategory,
        pageCategory: this.categoryAllData.metaTags.analyticsPageData.pageCategory,
        pageSubCategory: this.categoryAllData.metaTags.analyticsPageData.pageSubCategory,
        pageSubSubCategory: this.categoryAllData.metaTags.analyticsPageData.pageSubSubCategory,
        pageCategoryId: this.categoryAllData.metaTags.analyticsPageData.pageCategoryId,
        pageSubCategoryId: this.categoryAllData.metaTags.analyticsPageData.pageSubCategoryId,
        pageRangeId: this.categoryAllData.metaTags.analyticsPageData.pageRangeId,
        pageProductId: this.categoryAllData.metaTags.analyticsPageData.pageProductId,
      },
    );
  }

  private async init() {
    const [categoryAllData, hmcBanner] = await Promise.all([
      this.loadCategoryAllData(),
      this.getHmcVisibilityInfo(),
    ]);

    if (!categoryAllData) return;

    if (categoryAllData.config?.context?.template === CategoryContextTemplate.listing) {
      categoryAllData[CategoryInfoObject.listingInfo].hmcBanner = hmcBanner;
    } else {
      categoryAllData[CategoryInfoObject.mainInfo].hmcBanner = hmcBanner;
    }

    this.categoryAllData = categoryAllData;
    this.recentlyViewedConfig = categoryAllData.recentlyViewedConfig;
    if (this.categoryAllData?.metaTags?.analyticsPageData) this.emitPageViewEvent();

    if (this.showRecentlyViewed && this.recentlyViewedConfig) {
      const { data, sectionTitle } = await requestRecentlyViewedProducts(
        this.recentlyViewedConfig.maxProductsToDisplay || MAX_RECENTLY_VIEW_PRODUCTS,
        this.recentlyViewedConfig.sectionTitle,
      );
      this.recentlyViewedProducts = {
        data,
        sectionTitle,
        versionLabel: this.recentlyViewedProducts.versionLabel || '',
      };
    }
  }

  private buildFilterSearchParam() {
    return FILTER_QUERY_PARAM in this.searchParams
      ? `&${FILTER_QUERY_PARAM}=${this.searchParams[FILTER_QUERY_PARAM]}`
      : '';
  }

  private async loadCategoryAllData(): Promise<TCategoryAllData | TCategoryListingAllData> {
    if (!this.categoryId || !this.countryCode || !this.languageCode) return;

    try {
      const categoriesAllEndpointURL = `${getExpAPIEndpointURL(
        expAPIEndpoint.CATEGORIES_ALL_SPRYKER,
        {
          categoryId: this.categoryId,
        },
      )}${this.buildFilterSearchParam()}`;
      const res: TCategoryAllData | TCategoryListingAllData = await getData(
        false,
        categoriesAllEndpointURL,
      );

      return res;
    } catch (err) {
      this.pesLinkClick.emit({ page: pesCatalogPageTypes.ALL_PRODUCTS });
      consoleErrorMsg(err, 'Error occured while executing loadCategoryAllData()');
    }
  }

  private async getHmcVisibilityInfo(): Promise<THMCBanner> {
    try {
      const { error, result } = await HmcCategories.fetchSingleCategoryWithHMC({
        countryCode: this.countryCode,
        languageCode: this.languageCode,
        queryParams: {
          pimId: this.categoryId,
          platform: HMCPlatform.MySchneider,
        },
      });

      if (error) {
        throw error;
      }

      return result.hmcBanner;
    } catch (err) {
      console.error(`[PES]: HMC visibility api call failed ${err.message || err}`);
    }
  }

  render(): HTMLElement {
    if (!this.categoryAllData) {
      return <pdp-skeleton />;
    }

    return (
      <Fragment>
        { this.categoryAllData.config?.context?.template === CategoryContextTemplate.category ? (
            <pes-app-category
              host={this.host}
              categoryAllData={this.categoryAllData as TCategoryAllData}
              showBreadcrumbs={this.showBreadcrumbs}
              categoryId={this.categoryId}
              countryCode={this.countryCode}
              languageCode={this.languageCode}
            />
        ) : (
            <pes-app-spryker-category-listing
              host={this.host}
              categoryAllData={this.categoryAllData as TCategoryListingAllData}
              showBreadcrumbs={this.showBreadcrumbs}
              categoryId={this.categoryId}
              showDocuments={this.showDocuments}
              enableCommerceConnector={this.enableCommerceConnector}
              showBuyOnline={this.showBuyOnline}
              showCompare={this.showCompare}
              searchParams={this.searchParams}
              isPBS={true}
              productCtas={this.productCtas}
              countryCode={this.countryCode}
              languageCode={this.languageCode}
            />
        )
        }
        <pes-products-carousel
          {...this.recentlyViewedProducts}
          hostWithLocale={`${this.host}${this.countryCode}/${this.languageCode}/`}
          pageType={PageNames.CATEGORY}
          pageId={this.categoryId}
          class="pes-app-category__section"
        ></pes-products-carousel>
      </Fragment>
    );
  }
}
